import {
  Box,
  Button,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import ArrowBack from '@material-ui/icons/ArrowBack';
import CloseIcon from '@material-ui/icons/Close';
import CreateIcon from '@material-ui/icons/Create';
import SaveIcon from '@material-ui/icons/Save';
import clsx from 'clsx';
import moment from 'moment-timezone';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { z } from 'zod';
import { getRules } from '../../../../actions/rules.actions';
import { setLoading } from '../../../../actions/system.actions';
import {
  addUnitGuestVehicleSlot,
  addUnitTenantVehicleSlot,
  deleteUnitGuestPermit,
  deleteUnitTenantPermit,
  getAddendumPdf,
  getUnit,
  removeUnitGuestVehicleSlot,
  removeUnitTenantVehicleSlot,
  updateUnitContact,
  updateUnitGuestPermit,
  updateUnitNumber,
  updateUnitStatusSection,
  updateUnitTenantPermit,
} from '../../../../actions/units.actions';
import { ParkingAddendumIcon } from '../../../../Icons';
import VppDatePicker from '../../../../components/datePicker/vppDatePicker';
import { formatValidationErrors, getApiErrorMessage } from '../../../../utils';
import {
  formatPermitFieldErrors,
  reshapeBackendPermitErrors,
  resendActivationLinkSchema,
  unitPermitSchema,
} from '../../../../validators';
import { normalizeLicenceState } from './licenceStateUtils';
import ArchiveUnit from '../archive/ArchiveUnit';
import UnitsContactInformationBlock from './ContactInfoBlock';
import ResendActivationLink from './ResendActivationLink';
import UnitStatusMetaField from './UnitStatusMetaField';
import VehiclesSection from './VehiclesSection';

const UNIT_STATUS_SELECT_OPTIONS = ['active', 'blocked', 'suspended'].map(
  (value) => ({ name: value, value })
);

const EDITABLE_UNIT_STATUSES = ['active', 'blocked', 'suspended'];

const padVehicleSlots = (vehicles, count) => {
  const list = Array.isArray(vehicles) ? [...vehicles] : [];
  const target = Math.max(list.length, parseInt(count, 10) || 0);

  if (target <= list.length) {
    return list;
  }

  // Unique objects per slot — shared {} from fill({}) mutates across cards.
  return [
    ...list,
    ...Array.from({ length: target - list.length }, () => ({})),
  ];
};

const FIELD_BORDER = '#F7F8FA';
const FIELD_BG = '#F7F8FC';
const FIELD_BORDER_EDIT = '#D0D5DD';
const META_ACCENT = '#003E7E';

const fieldOutline = {
  borderColor: `${FIELD_BORDER} !important`,
  borderWidth: '1px !important',
  boxShadow: 'none !important',
};

const fieldOutlineEdit = {
  borderColor: `${FIELD_BORDER_EDIT} !important`,
  borderWidth: '1px !important',
  boxShadow: 'none !important',
};

const useStyles = makeStyles((theme) => ({
  backButtonText: {
    textTransform: 'capitalize',
    color: '#BDBDBD',
  },
  backButtonIcon: {
    fontSize: '34px!important', // TODO: Refactor needed
    left: '-8px',

    position: 'relative',
  },
  unitNumberRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minHeight: 40,
  },
  // Keep field width fixed so helper/error text cannot push icons right
  unitNumberFieldWrap: {
    width: 140,
    flexShrink: 0,
  },
  unitNumberError: {
    display: 'block',
    marginTop: theme.spacing(0.5),
    maxWidth: 220,
    color: theme.palette.error.main,
    lineHeight: 1.3,
  },
  // Soft solid fill — design uses flat #F7F8FC (not a gradient)
  unitNumberField: {
    width: 140,
    height: 40,
    fontSize: 20,
    color: META_ACCENT,
    fontWeight: 700,
    backgroundColor: `${FIELD_BG} !important`,
    backgroundImage: 'none',
    borderRadius: theme.shape.borderRadius,
    '& .MuiOutlinedInput-notchedOutline': fieldOutline,
    '&:hover .MuiOutlinedInput-notchedOutline': fieldOutline,
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': fieldOutline,
  },
  unitNumberFieldEditing: {
    '& .MuiOutlinedInput-notchedOutline': fieldOutlineEdit,
    '&:hover .MuiOutlinedInput-notchedOutline': fieldOutlineEdit,
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': fieldOutlineEdit,
  },
  unitNumberFieldInput: {
    textAlign: 'center',
    paddingTop: '8px',
    paddingBottom: '8px',
    backgroundColor: 'transparent !important',
    backgroundImage: 'none',
    color: META_ACCENT,
    fontWeight: 700,
    fontSize: 20,
  },
  unitNumberEditIcon: {
    color: META_ACCENT,
    padding: 4,
  },
  unitMetaRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  unitMetaLeft: {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: theme.spacing(4),
  },
  unitMetaField: {
    display: 'flex',
    flexDirection: 'column',
  },
  unitMetaLabel: {
    marginBottom: theme.spacing(0.75),
    color: META_ACCENT,
    fontSize: '0.9375rem', // 15px — caption was too small next to meta fields
    fontWeight: 500,
    lineHeight: 1.3,
    minHeight: 20,
  },
  // Soft #F7F8FC fill + near-invisible border (design parity)
  metaFieldBorder: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: `${FIELD_BG} !important`,
      backgroundImage: 'none',
    },
    '& .MuiOutlinedInput-input': {
      backgroundColor: 'transparent !important',
      backgroundImage: 'none',
    },
    '& .MuiOutlinedInput-root:not(.Mui-error) .MuiOutlinedInput-notchedOutline': fieldOutline,
    '& .MuiOutlinedInput-root:not(.Mui-error):hover .MuiOutlinedInput-notchedOutline': fieldOutline,
    '& .MuiOutlinedInput-root:not(.Mui-error).Mui-focused .MuiOutlinedInput-notchedOutline': fieldOutline,
    '& .MuiTextField-root .MuiOutlinedInput-root:not(.Mui-error) .MuiOutlinedInput-notchedOutline': fieldOutline,
    '& .MuiTextField-root:hover .MuiOutlinedInput-root:not(.Mui-error) .MuiOutlinedInput-notchedOutline': fieldOutline,
    '& .MuiTextField-root .MuiOutlinedInput-root:not(.Mui-error).Mui-focused .MuiOutlinedInput-notchedOutline': fieldOutline,
  },
  metaFieldEditing: {
    '& .MuiOutlinedInput-root:not(.Mui-error) .MuiOutlinedInput-notchedOutline': fieldOutlineEdit,
    '& .MuiOutlinedInput-root:not(.Mui-error):hover .MuiOutlinedInput-notchedOutline': fieldOutlineEdit,
    '& .MuiOutlinedInput-root:not(.Mui-error).Mui-focused .MuiOutlinedInput-notchedOutline': fieldOutlineEdit,
    '& .MuiTextField-root .MuiOutlinedInput-root:not(.Mui-error) .MuiOutlinedInput-notchedOutline': fieldOutlineEdit,
    '& .MuiTextField-root:hover .MuiOutlinedInput-root:not(.Mui-error) .MuiOutlinedInput-notchedOutline': fieldOutlineEdit,
    '& .MuiTextField-root .MuiOutlinedInput-root:not(.Mui-error).Mui-focused .MuiOutlinedInput-notchedOutline': fieldOutlineEdit,
  },
  // Issue date — same soft solid box as unit number; muted value + grey calendar
  issueDateField: {
    minWidth: 180,
    '& .MuiOutlinedInput-root': {
      height: 40,
      backgroundColor: `${FIELD_BG} !important`,
      backgroundImage: 'none',
    },
    '& .MuiOutlinedInput-root.Mui-disabled': {
      backgroundColor: `${FIELD_BG} !important`,
      backgroundImage: 'none',
    },
    '& .MuiOutlinedInput-input': {
      backgroundColor: 'transparent !important',
      backgroundImage: 'none',
      color: '#9AA0A6',
      WebkitTextFillColor: '#9AA0A6',
    },
    '& .MuiOutlinedInput-input.Mui-disabled': {
      backgroundColor: 'transparent !important',
      color: '#9AA0A6',
      WebkitTextFillColor: '#9AA0A6',
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': fieldOutline,
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': fieldOutline,
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': fieldOutline,
    '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': fieldOutline,
    '& .MuiIconButton-root': {
      color: '#9AA0A6',
    },
    '& .MuiIconButton-root.Mui-disabled': {
      color: '#9AA0A6',
    },
  },
  parkingAddendumButton: {
    color: META_ACCENT,
    borderColor: META_ACCENT,
    '&:hover': {
      borderColor: '#003266',
      backgroundColor: 'rgba(0, 62, 126, 0.04)',
    },
    '&.Mui-disabled': {
      borderColor: 'rgba(0, 62, 126, 0.3)',
      color: 'rgba(0, 62, 126, 0.3)',
    },
    '&.Mui-disabled .MuiButton-startIcon img': {
      opacity: 0.35,
    },
  },
  // Shared offset so Parking Addendum + Resend Activation Link stay level
  // with unit meta inputs (below their labels)
  unitMetaActions: {
    marginLeft: 'auto',
    marginTop: 26,
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  // Same navy outline as Parking Addendum (no top margin)
  archiveButton: {
    color: META_ACCENT,
    borderColor: META_ACCENT,
    '&:hover': {
      borderColor: '#003266',
      backgroundColor: 'rgba(0, 62, 126, 0.04)',
    },
  },
}));

moment.tz.setDefault('America/Chicago');

const getExpirationDate = (guest_hours_start, guest_hours_finish, vehicle) => {
  if (!guest_hours_start || !guest_hours_finish) {
    console.log({ guest_hours_start, guest_hours_finish });
    return;
  }
  // get the hours and minutes from vehicle.expiration_date
  if (guest_hours_start === guest_hours_finish) {
    let time = moment(vehicle.issue_date).format('H:mm').split(':');
    return moment(vehicle.expiration_date)
      .set({ hours: time[0], minutes: time[1] })
      .toDate();
  } else {
    let time = guest_hours_finish.split(':');
    return moment(vehicle.expiration_date)
      .set({ hours: time[0], minutes: time[1] })
      .toDate();
  }
};

// Contact Edit unlocks name / phone / email only.
// Permitted vehicle counts are display-only (updated via Add Permit slot APIs).
// Unit status uses its own header edit + PATCH /units/<id>/status/.
const CONTACT_SECTION_FIELDS = [
  'first_name',
  'last_name',
  'email',
  'phone_number',
];

const EditUnits = ({ match }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const dispatch = useDispatch();
  const rootRef = useRef(null);
  const system = useSelector((state) => state.system);
  const rules = useSelector((state) => state.rules);
  const { enqueueSnackbar } = useSnackbar();

  const [isArchived, setIsArchived] = useState(true);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [isContactEditMode, setIsContactEditMode] = useState(false);
  const [isUnitNumberEditMode, setIsUnitNumberEditMode] = useState(false);
  const [isUnitStatusEditMode, setIsUnitStatusEditMode] = useState(false);
  const [tenantVehicles, setTenantVehicles] = useState([]);
  const [guestVehicles, setGuestVehicles] = useState([]);
  const [vehiclesRevision, setVehiclesRevision] = useState(0);
  const [reservedSpots, setReservedSpots] = useState([]);
  const [tenantVehiclesNumber, setTenantVehiclesNumber] = useState(null);
  const [guestVehiclesNumber, setGuestVehiclesNumber] = useState(null);
  const [status, setStatus] = useState('');
  const [isUnitLoaded, setIsUnitLoaded] = useState(false);
  const [disabledAddendum, setDisabledAddendum] = useState(false);
  const [newActivationData, setNewActivationData] = useState({});
  const savedUnitStatusRef = useRef('');

  const history = useHistory();

  const serializeDate = (value) => {
    if (!value) {
      return value;
    }
    if (moment.isMoment(value)) {
      return value.toISOString();
    }
    if (value instanceof Date) {
      return value.toISOString();
    }
    return value;
  };

  const {
    errors,
    register,
    setValue,
    getValues,
    control,
    watch,
    clearErrors,
    trigger,
    reset,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onBlur',
    defaultValues: {
      status: '',
    },
  });
  const { unitId } = match.params;
  const { guest_hours_finish, guest_hours_start } = rules;

  const getData = useCallback(() => {
    window.scrollTo(0, 0);
    setIsUnitLoaded(false);
    // Keep prior status visible during the loader — do not blank the header.
    dispatch(setLoading(true));
    return dispatch(getUnit(unitId))
      .then(({ data }) => {
        setDisabledAddendum(data.status !== 'active');
        setIsArchived(data.status === 'archived');
        // Saved permits come back first from the API. Pad empty slots after them
        // and write those padded arrays into the form so a save at index 2 cannot
        // leave stale plate/state values on the empty cards that take its place.
        const paddedTenantVehicles = padVehicleSlots(
          data.tenant_vehicles,
          data.tenant_vehicles_number
        );
        const paddedGuestVehicles = padVehicleSlots(
          data.guest_vehicles,
          data.daily_guest_vehicles_number
        );
        setTenantVehicles(paddedTenantVehicles);
        setGuestVehicles(paddedGuestVehicles);
        setTenantVehiclesNumber(data.tenant_vehicles_number);
        setGuestVehiclesNumber(data.daily_guest_vehicles_number);
        Object.keys(data).forEach((item) => {
          // Padded vehicle arrays are written below — skip raw API lists here.
          if (item === 'tenant_vehicles' || item === 'guest_vehicles') {
            return;
          }
          setValue(item, data[item]);
          if (item === 'status') {
            const nextStatus = String(data[item] || '')
              .toLowerCase()
              .trim();
            setValue('status', nextStatus);
            setStatus(nextStatus);
            savedUnitStatusRef.current = nextStatus;
            return;
          }
          if (item === 'reserved_spots') {
            setReservedSpots(data[item]);
          }
          if (
            item === 'first_name' ||
            item === 'last_name' ||
            item === 'phone_number' ||
            item === 'email' ||
            item === 'unit_number'
          ) {
            setNewActivationData((prev) => ({
              ...prev,
              [item]: data[item],
            }));
          }
        });
        setValue('tenant_vehicles_number', data.tenant_vehicles_number);
        setValue('daily_guest_vehicles_number', data.daily_guest_vehicles_number);
        // Full form reset for vehicle arrays so nested RHForm fields at old
        // indices (plate/state/make) cannot survive after a save-shift pad.
        reset(
          {
            ...getValues(),
            ...data,
            status: String(data.status || '')
              .toLowerCase()
              .trim(),
            tenant_vehicles_number: data.tenant_vehicles_number,
            daily_guest_vehicles_number: data.daily_guest_vehicles_number,
            tenant_vehicles: paddedTenantVehicles,
            guest_vehicles: paddedGuestVehicles,
          },
          { keepDefaultValues: false }
        );
        setVehiclesRevision((revision) => revision + 1);
        setIsUnitLoaded(true);
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  }, [dispatch, setValue, getValues, reset, unitId]);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    dispatch(getRules());
  }, [dispatch]);

  useEffect(() => {
    register('status');
  }, [register]);

  const handleDownloadAddemdumPdf = () => {
    dispatch(getAddendumPdf(unitId)).then(({ data }) => {
      window.open(data.parking_addendum, '_blank');
    });
  };

  const handleAddTenant = async () => {
    setSubmitDisabled(true);
    dispatch(setLoading(true));

    try {
      const { data } = await dispatch(addUnitTenantVehicleSlot(unitId));
      const newCount = data?.tenant_vehicles_number;
      // Live update Contact count box immediately from slot API response.
      if (newCount != null) {
        setTenantVehiclesNumber(newCount);
        setValue('tenant_vehicles_number', newCount);
      }
      // Refresh padded cards; new empty slot stays in view mode (user clicks Edit).
      await getData();
    } catch (error) {
      const errorData = error?.response?.data || {};
      enqueueSnackbar(
        getApiErrorMessage(errorData) || 'Error adding tenant permit slot.',
        { variant: 'error' }
      );
      dispatch(setLoading(false));
    } finally {
      setSubmitDisabled(false);
    }
  };

  const handleAddGuest = async () => {
    setSubmitDisabled(true);
    dispatch(setLoading(true));

    try {
      const { data } = await dispatch(addUnitGuestVehicleSlot(unitId));
      const newCount = data?.daily_guest_vehicles_number;
      // Live update Contact count box immediately from slot API response.
      if (newCount != null) {
        setGuestVehiclesNumber(newCount);
        setValue('daily_guest_vehicles_number', newCount);
      }
      // Refresh padded cards; new empty slot stays in view mode (user clicks Edit).
      await getData();
    } catch (error) {
      const errorData = error?.response?.data || {};
      enqueueSnackbar(
        getApiErrorMessage(errorData) || 'Error adding guest permit slot.',
        { variant: 'error' }
      );
      dispatch(setLoading(false));
    } finally {
      setSubmitDisabled(false);
    }
  };

  const prepareTenantVehicle = (vehicle) => {
    if (!vehicle?.issue_date) {
      return null;
    }
    const prepared = { ...vehicle };
    if (
      !vehicle.licence_plate ||
      !vehicle.color ||
      !vehicle.make ||
      !vehicle.model ||
      !vehicle.licence_state
    ) {
      prepared.expiration_date = '';
    } else {
      prepared.issue_date = serializeDate(vehicle.issue_date);
      prepared.expiration_date = serializeDate(vehicle.expiration_date);
    }
    return prepared;
  };

  const prepareGuestVehicle = (vehicle) => {
    if (!vehicle?.issue_date) {
      return null;
    }
    const prepared = { ...vehicle };
    const expirationDate = getExpirationDate(
      guest_hours_start,
      guest_hours_finish,
      vehicle
    );
    if (
      !vehicle.licence_plate ||
      !vehicle.color ||
      !vehicle.make ||
      !vehicle.model ||
      !vehicle.licence_state
    ) {
      delete prepared.expiration_date;
    } else {
      prepared.expiration_date = serializeDate(expirationDate);
      prepared.issue_date = serializeDate(vehicle.issue_date);
    }
    return prepared;
  };

  // Contact PATCH: name / phone / email / reserved spots only.
  // Counts stay display-only (unit GET + Add Permit slot APIs).
  const prepareContactPayload = useCallback(
    (data) => {
      const payload = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone_number: data.phone_number,
      };

      if (rules?.allow_tenant_to_own_reserved_spots) {
        const rawSpots =
          data.reserved_spots !== undefined
            ? data.reserved_spots
            : reservedSpots;
        // Drop true placeholders (no id + empty spot) so remove-last → [] →
        // delete by omission. Keep id + blank spot so backend can 400 instead
        // of silently deleting on accidental clear.
        payload.reserved_spots = (Array.isArray(rawSpots) ? rawSpots : []).filter(
          (spot) =>
            spot &&
            (String(spot.id ?? '').trim() || String(spot.spot || '').trim())
        );
      }

      return payload;
    },
    [reservedSpots, rules?.allow_tenant_to_own_reserved_spots]
  );

  const savePermit = (type, vehicleIndex) => {
    const vehiclesKey =
      type === 'tenant' ? 'tenant_vehicles' : 'guest_vehicles';
    const vehicle = getValues()[vehiclesKey]?.[vehicleIndex] || {};

    try {
      unitPermitSchema.parse({
        ...vehicle,
        licence_plate: String(vehicle.licence_plate || '')
          .trim()
          .toUpperCase(),
        licence_state: normalizeLicenceState(vehicle.licence_state),
      });
    } catch (err) {
      if (err instanceof z.ZodError) {
        setFieldErrors(formatPermitFieldErrors(vehiclesKey, vehicleIndex, err));
        enqueueSnackbar(
          'Please correct the errors before saving.',
          { variant: 'error' }
        );
        return Promise.reject();
      }

      throw err;
    }

    const payload =
      type === 'tenant'
        ? prepareTenantVehicle(vehicle)
        : prepareGuestVehicle(vehicle);

    if (!payload) {
      enqueueSnackbar('Permit data is incomplete.', { variant: 'error' });
      return Promise.reject();
    }

    setFieldErrors({});
    setSubmitDisabled(true);
    dispatch(setLoading(true));

    const saveAction =
      type === 'tenant'
        ? updateUnitTenantPermit(unitId, payload)
        : updateUnitGuestPermit(unitId, payload);

    return dispatch(saveAction)
      .then(async () => {
        if (type === 'tenant') {
          setNewTenantPermitIndex(null);
        } else {
          setNewGuestPermitIndex(null);
        }
        // Keep the loader up through unit refresh so the new card is ready.
        await getData();
        enqueueSnackbar('Permit updated successfully', { variant: 'success' });
      })
      .catch((error) => {
        const errorData = error?.response?.data || {};
        setFieldErrors(
          reshapeBackendPermitErrors(errorData, vehiclesKey, vehicleIndex)
        );
        const apiMessage = getApiErrorMessage(errorData);
        enqueueSnackbar(
          apiMessage ||
            'Error updating permit. Please make sure all fields are filled in correctly.',
          { variant: 'error' }
        );
        dispatch(setLoading(false));
        return Promise.reject(error);
      })
      .finally(() => {
        setSubmitDisabled(false);
      });
  };

  const cancelPermit = (type, vehicleIndex) => {
    if (type === 'tenant') {
      setNewTenantPermitIndex(null);
    } else {
      setNewGuestPermitIndex(null);
    }
    setFieldErrors({});
    getData();
  };

  const getPermittedVehicleCount = (type) => {
    const value =
      type === 'tenant' ? tenantVehiclesNumber : guestVehiclesNumber;
    return Math.max(0, parseInt(value, 10) || 0);
  };

  const applyVehicleSlots = (type, vehicles, countOverride = null) => {
    const targetCount =
      countOverride != null
        ? Math.max(0, parseInt(countOverride, 10) || 0)
        : getPermittedVehicleCount(type);
    const padded = padVehicleSlots(vehicles, targetCount);
    if (type === 'tenant') {
      setTenantVehicles(padded);
      setNewTenantPermitIndex(null);
    } else {
      setGuestVehicles(padded);
      setNewGuestPermitIndex(null);
    }
    setValue(`${type}_vehicles`, padded);
  };

  const handleDeleteVehicle = async (index, type, savedVehicleId = null) => {
    const vehicleIndex = Number(index);
    const stateVehicles = type === 'tenant' ? tenantVehicles : guestVehicles;
    const formVehicles = getValues(`${type}_vehicles`) || [];
    const selectedVehicle = {
      ...(stateVehicles[vehicleIndex] || {}),
      ...(formVehicles[vehicleIndex] || {}),
    };

    const normalizePermitId = (value) => {
      if (value === null || value === undefined || value === '') {
        return null;
      }
      const asNumber = Number(value);
      if (Number.isFinite(asNumber) && asNumber > 0) {
        return asNumber;
      }
      return null;
    };

    // Prefer explicit id, then state/form. Fall back to plate match in section.
    let permitId =
      normalizePermitId(savedVehicleId) ||
      normalizePermitId(selectedVehicle.id) ||
      normalizePermitId(stateVehicles[vehicleIndex]?.id);

    if (!permitId) {
      const plate = String(
        selectedVehicle.licence_plate ||
          stateVehicles[vehicleIndex]?.licence_plate ||
          ''
      )
        .trim()
        .toUpperCase();
      if (plate) {
        const match = stateVehicles.find(
          (item) =>
            item?.id &&
            String(item.licence_plate || '')
              .trim()
              .toUpperCase() === plate
        );
        permitId = normalizePermitId(match?.id);
      }
    }

    // Only server-synced slot fields prove a saved permit. Live form values
    // (typed Make / plate on an unsaved Add Permit card) must not block slot DELETE.
    const serverVehicle = stateVehicles[vehicleIndex] || {};
    const looksLikeSavedPermit = Boolean(
      permitId ||
        serverVehicle.licence_plate ||
        serverVehicle.make
    );

    // Never "succeed" via slot DELETE when the card has permit data but no id —
    // that left DB rows intact and kept guest count at 22 after refresh.
    if (!permitId && looksLikeSavedPermit) {
      enqueueSnackbar(
        'Cannot delete this permit (missing id). Refresh the page and try again.',
        { variant: 'error' }
      );
      await getData();
      return;
    }

    const remainingVehicles = stateVehicles.filter(
      (_, slotIndex) => slotIndex !== vehicleIndex
    );
    const savedRemaining = remainingVehicles.filter((item) => item?.id).length;
    const currentCount = getPermittedVehicleCount(type);
    const nextCount = Math.max(
      savedRemaining,
      currentCount > 0 ? currentCount - 1 : 0
    );

    const syncLocalCount = (count) => {
      if (type === 'tenant') {
        setTenantVehiclesNumber(count);
        setValue('tenant_vehicles_number', count);
      } else {
        setGuestVehiclesNumber(count);
        setValue('daily_guest_vehicles_number', count);
      }
    };

    setSubmitDisabled(true);
    dispatch(setLoading(true));

    syncLocalCount(nextCount);
    applyVehicleSlots(type, remainingVehicles, nextCount);
    setFieldErrors({});
    clearErrors(`${type}_vehicles`);
    setVehiclesRevision((revision) => revision + 1);

    try {
      if (permitId) {
        const response =
          type === 'guest'
            ? await dispatch(deleteUnitGuestPermit(unitId, permitId))
            : await dispatch(deleteUnitTenantPermit(unitId, permitId));
        const payload = response?.data || {};
        const apiCount =
          type === 'guest'
            ? payload.daily_guest_vehicles_number
            : payload.tenant_vehicles_number;
        if (apiCount != null) {
          syncLocalCount(apiCount);
        }
      } else if (type === 'guest') {
        const { data } = await dispatch(removeUnitGuestVehicleSlot(unitId));
        if (data?.daily_guest_vehicles_number != null) {
          syncLocalCount(data.daily_guest_vehicles_number);
        }
      } else {
        const { data } = await dispatch(removeUnitTenantVehicleSlot(unitId));
        if (data?.tenant_vehicles_number != null) {
          syncLocalCount(data.tenant_vehicles_number);
        }
      }

      await getData();
      enqueueSnackbar('Permit deleted successfully', { variant: 'success' });
    } catch (error) {
      const errorData = error?.response?.data || {};
      enqueueSnackbar(
        getApiErrorMessage(errorData) || 'Error deleting permit.',
        { variant: 'error' }
      );
      await getData();
      dispatch(setLoading(false));
    } finally {
      setSubmitDisabled(false);
    }
  };

  const handleCancelSection = (section) => {
    if (section === 'contact') {
      setIsContactEditMode(false);
      setIsUnitNumberEditMode(false);
      setIsUnitStatusEditMode(false);
    }
    setFieldErrors({});
    getData();
  };

  const handleCancelUnitNumber = () => {
    setIsUnitNumberEditMode(false);
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.unit_number;
      return next;
    });
    getData();
  };

  const handleSaveUnitNumber = async () => {
    const unitNumber = getValues('unit_number');
    const trimmed =
      unitNumber === null || unitNumber === undefined
        ? ''
        : String(unitNumber).trim();

    if (!trimmed) {
      setFieldErrors((prev) => ({
        ...prev,
        unit_number: 'Unit number is required.',
      }));
      return;
    }

    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.unit_number;
      return next;
    });
    setSubmitDisabled(true);
    dispatch(setLoading(true));

    try {
      await dispatch(updateUnitNumber(unitId, { unit_number: trimmed }));
      enqueueSnackbar('Unit number updated successfully', {
        variant: 'success',
      });
      setIsUnitNumberEditMode(false);
      await getData();
    } catch (error) {
      const errorData = error?.response?.data || {};
      setFieldErrors(errorData);
      enqueueSnackbar(
        getApiErrorMessage(errorData) || 'Error updating unit number.',
        { variant: 'error' }
      );
      dispatch(setLoading(false));
    } finally {
      setSubmitDisabled(false);
    }
  };

  const handleCancelUnitStatus = () => {
    const restored = String(savedUnitStatusRef.current || status || '')
      .toLowerCase()
      .trim();
    if (restored) {
      setValue('status', restored);
      setStatus(restored);
    }
    setIsUnitStatusEditMode(false);
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.status;
      return next;
    });
    getData();
  };

  const handleSaveUnitStatus = async () => {
    const nextStatus = String(getValues('status') || '')
      .toLowerCase()
      .trim();

    if (!EDITABLE_UNIT_STATUSES.includes(nextStatus)) {
      setFieldErrors((prev) => ({
        ...prev,
        status: 'Select an active, blocked, or suspended status.',
      }));
      return;
    }

    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.status;
      return next;
    });
    setSubmitDisabled(true);
    dispatch(setLoading(true));

    try {
      await dispatch(updateUnitStatusSection(unitId, { status: nextStatus }));
      enqueueSnackbar('Unit status updated successfully', {
        variant: 'success',
      });
      setIsUnitStatusEditMode(false);
      setStatus(nextStatus);
      savedUnitStatusRef.current = nextStatus;
      setValue('status', nextStatus);
      setDisabledAddendum(nextStatus !== 'active');
      await getData();
    } catch (error) {
      const errorData = error?.response?.data || {};
      setFieldErrors(errorData);
      enqueueSnackbar(
        getApiErrorMessage(errorData) || 'Error updating unit status.',
        { variant: 'error' }
      );
      dispatch(setLoading(false));
    } finally {
      setSubmitDisabled(false);
    }
  };

  const onSubmit = useCallback(
    (data, section) => {
      setFieldErrors({});

      let savePromise;

      try {
        if (section === 'contact') {
          resendActivationLinkSchema.parse(data);

          // Contact API only — no unit number, counts, status, or vehicle arrays.
          savePromise = dispatch(
            updateUnitContact(unitId, prepareContactPayload(data))
          );
        }

        if (!savePromise) {
          return;
        }

        setSubmitDisabled(true);
        dispatch(setLoading(true));

        savePromise
          .then(() => {
            enqueueSnackbar('Unit updated successfully', {
              variant: 'success',
            });
            setIsContactEditMode(false);
            setIsUnitNumberEditMode(false);
            setIsUnitStatusEditMode(false);
            getData();
            rootRef.current?.scrollIntoView?.({ behavior: 'smooth' });
          })
          .catch((error) => {
            const errorData = error?.response?.data || {};
            setFieldErrors(errorData);
            const apiMessage = getApiErrorMessage(errorData);
            enqueueSnackbar(
              apiMessage ||
                'Error updating unit. Please make sure all fields are filled in correctly.',
              {
                variant: 'error',
              }
            );
            rootRef.current?.scrollIntoView?.({ behavior: 'smooth' });
          })
          .finally(() => {
            setSubmitDisabled(false);
            dispatch(setLoading(false));
          });
      } catch (err) {
        if (err instanceof z.ZodError) {
          window.scrollTo(0, 0);
          setFieldErrors(formatValidationErrors(err));
        }
      }
    },
    [dispatch, enqueueSnackbar, getData, prepareContactPayload, unitId]
  );

  const handleSaveContact = async () => {
    // Validate only contact-section fields so an open/unsaved permit card
    // cannot silently block contact save via shared-form validation.
    const isValid = await trigger(CONTACT_SECTION_FIELDS);
    if (!isValid) {
      return;
    }
    onSubmit(getValues(), 'contact');
  };

  // Prefer live form/state; fall back to last saved so the header does not
  // flash empty while getData() sets isUnitLoaded=false under the loader.
  const normalizedUnitStatus = String(
    watch('status') || status || savedUnitStatusRef.current || ''
  )
    .toLowerCase()
    .trim();
  // Do not gate on isUnitLoaded — that flickers the pencil off during refresh.
  const canEditUnitStatus =
    !isArchived &&
    normalizedUnitStatus &&
    normalizedUnitStatus !== 'archived' &&
    normalizedUnitStatus !== 'pending' &&
    EDITABLE_UNIT_STATUSES.includes(normalizedUnitStatus);

  return (
    <Box mr={4} pb={4}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        ref={rootRef}
      >
        <Button
          onClick={() => {
            history.goBack();
          }}
          startIcon={
            <ArrowBack color="primary" className={classes.backButtonIcon} />
          }
        >
          <Typography color="textSecondary" className={classes.backButtonText}>
            Back
          </Typography>
        </Button>
      </Box>
      <Box mt={2} mx={-4}>
        <Divider />
      </Box>
      <form
        onSubmit={(event) => {
          // Section saves use explicit buttons. Never let Enter / icon buttons
          // submit this wrapper form (that silently blocked permit delete).
          event.preventDefault();
        }}
      >
        <Box my={3}>
          <Box className={classes.unitMetaRow} mb={3} pl={3} pr={1}>
            <Box className={classes.unitMetaLeft}>
              <Box className={classes.unitMetaField}>
                <Typography
                  variant="caption"
                  className={classes.unitMetaLabel}
                >
                  Unit Number
                </Typography>
                <Box>
                  <Box className={classes.unitNumberRow}>
                    <TextField
                      className={clsx(
                        classes.unitNumberFieldWrap,
                        classes.metaFieldBorder,
                        isUnitNumberEditMode && classes.metaFieldEditing
                      )}
                      name="unit_number"
                      size="small"
                      inputRef={register}
                      error={!!fieldErrors.unit_number}
                      InputProps={{
                        classes: {
                          root: clsx(
                            classes.unitNumberField,
                            isUnitNumberEditMode && classes.unitNumberFieldEditing
                          ),
                          input: classes.unitNumberFieldInput,
                        },
                        readOnly: !isUnitNumberEditMode,
                      }}
                    />
                    {isUnitNumberEditMode ? (
                      <>
                        <Tooltip title="Cancel">
                          <IconButton
                            id="unit_number_cancel_button"
                            aria-label="Cancel unit number edit"
                            className={classes.unitNumberEditIcon}
                            size="small"
                            onClick={handleCancelUnitNumber}
                            disabled={submitDisabled}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Save">
                          <span>
                            <IconButton
                              id="unit_number_save_button"
                              aria-label="Save unit number"
                              className={classes.unitNumberEditIcon}
                              size="small"
                              color="secondary"
                              onClick={handleSaveUnitNumber}
                              disabled={submitDisabled}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    ) : (
                      <IconButton
                        id="unit_number_edit_button"
                        aria-label="Edit unit number"
                        className={classes.unitNumberEditIcon}
                        size="small"
                        onClick={() => setIsUnitNumberEditMode(true)}
                      >
                        <CreateIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  {fieldErrors.unit_number ? (
                    <Typography
                      variant="caption"
                      className={classes.unitNumberError}
                      role="alert"
                    >
                      {Array.isArray(fieldErrors.unit_number)
                        ? fieldErrors.unit_number.join(', ')
                        : fieldErrors.unit_number}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
              <Box className={classes.unitMetaField}>
                <Typography
                  variant="caption"
                  className={classes.unitMetaLabel}
                >
                  Unit Status
                </Typography>
                {!normalizedUnitStatus ? null : (
                  <Box className={classes.unitNumberRow}>
                    <UnitStatusMetaField
                      status={normalizedUnitStatus}
                      editable={isUnitStatusEditMode && canEditUnitStatus}
                      control={control}
                      name="status"
                      options={UNIT_STATUS_SELECT_OPTIONS}
                      id="unit_status_select"
                    />
                    {isUnitStatusEditMode ? (
                      <>
                        <Tooltip title="Cancel">
                          <IconButton
                            id="unit_status_cancel_button"
                            aria-label="Cancel unit status edit"
                            className={classes.unitNumberEditIcon}
                            size="small"
                            onClick={handleCancelUnitStatus}
                            disabled={submitDisabled}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Save">
                          <span>
                            <IconButton
                              id="unit_status_save_button"
                              aria-label="Save unit status"
                              className={classes.unitNumberEditIcon}
                              size="small"
                              color="secondary"
                              onClick={handleSaveUnitStatus}
                              disabled={submitDisabled}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    ) : canEditUnitStatus ? (
                      <IconButton
                        id="unit_status_edit_button"
                        aria-label="Edit unit status"
                        className={classes.unitNumberEditIcon}
                        size="small"
                        color="primary"
                        onClick={() => {
                          // Keep form in sync — display can show blocked from
                          // React state while watch('status') is still ''.
                          setValue('status', normalizedUnitStatus);
                          setIsUnitStatusEditMode(true);
                        }}
                        disabled={submitDisabled}
                      >
                        <CreateIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                  </Box>
                )}
                {fieldErrors.status ? (
                  <Typography variant="caption" color="error">
                    {Array.isArray(fieldErrors.status)
                      ? fieldErrors.status.join(', ')
                      : fieldErrors.status}
                  </Typography>
                ) : null}
              </Box>
              <Box className={classes.unitMetaField}>
                <Typography
                  variant="caption"
                  className={classes.unitMetaLabel}
                >
                  Issue Date
                </Typography>
                <Box className={classes.issueDateField}>
                  <VppDatePicker
                    className={classes.issueDateField}
                    control={control}
                    name="issue_date"
                    disabled
                    inputVariant="outlined"
                  />
                </Box>
              </Box>
            </Box>
            <Box className={classes.unitMetaActions}>
              <Button
                variant="outlined"
                size="large"
                className={classes.parkingAddendumButton}
                startIcon={<ParkingAddendumIcon width={18} height={18} />}
                onClick={() => handleDownloadAddemdumPdf()}
                disabled={disabledAddendum}
              >
                Parking Addendum
              </Button>
              {isUnitLoaded && status === 'pending' && (
                <ResendActivationLink
                  unitId={unitId}
                  newActivationData={newActivationData}
                  onSuccess={getData}
                />
              )}
            </Box>
          </Box>

          <UnitsContactInformationBlock
            inputRefs={register}
            fieldErrors={fieldErrors}
            disabled={!isContactEditMode}
            isEditing={isContactEditMode}
            onEdit={() => setIsContactEditMode(true)}
            onCancel={() => handleCancelSection('contact')}
            onSave={() => {
              clearErrors();
              handleSaveContact();
            }}
            showActions={!isArchived}
            submitDisabled={submitDisabled}
            reservedSpots={reservedSpots}
            setReservedSpots={setReservedSpots}
            getValues={getValues}
            setValue={setValue}
            watch={watch}
            isUnitLoaded={isUnitLoaded}
            tenantVehiclesNumber={tenantVehiclesNumber}
            guestVehiclesNumber={guestVehiclesNumber}
          />

          <VehiclesSection
            type="tenant"
            title="Permitted Tenant Vehicles"
            control={control}
            register={register}
            vehicles={tenantVehicles}
            fieldErrors={fieldErrors}
            vehicleTitle="Vehicle"
            buttonId="add_tenant_permit_button"
            addButtonText="Add Permit"
            addButtonHandler={handleAddTenant}
            deleteButtonHandler={handleDeleteVehicle}
            showActions={!isArchived}
            submitDisabled={submitDisabled}
            onSavePermit={(index) => savePermit('tenant', index)}
            onCancelPermit={(index) => cancelPermit('tenant', index)}
            watch={watch}
            getValues={getValues}
            setValue={setValue}
            formErrors={errors}
            isUnitLoaded={isUnitLoaded}
            vehiclesRevision={vehiclesRevision}
          />

          <VehiclesSection
            type="guest"
            title="Permitted Daily Guest Vehicles"
            control={control}
            register={register}
            vehicles={guestVehicles}
            fieldErrors={fieldErrors}
            buttonId="add_guest_permit_button"
            vehicleTitle="Guest Vehicle"
            addButtonText="Add Permit"
            addButtonHandler={handleAddGuest}
            deleteButtonHandler={handleDeleteVehicle}
            showActions={!isArchived}
            submitDisabled={submitDisabled}
            onSavePermit={(index) => savePermit('guest', index)}
            onCancelPermit={(index) => cancelPermit('guest', index)}
            watch={watch}
            getValues={getValues}
            setValue={setValue}
            formErrors={errors}
            isUnitLoaded={isUnitLoaded}
            vehiclesRevision={vehiclesRevision}
          />

          <Box display="flex" alignItems="center" mt={4} pl={3}>
            {!isArchived ? (
              <ArchiveUnit
                unitId={unitId}
                renderOpen={(handleClickOpen) => (
                  <Button
                    id="unit_archive_button"
                    variant="outlined"
                    size="large"
                    className={classes.archiveButton}
                    onClick={handleClickOpen}
                  >
                    Archive
                  </Button>
                )}
              />
            ) : !system.loading ? (
              <Typography variant="h6" color="primary">
                Archived
              </Typography>
            ) : null}
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default EditUnits;
